/**
 * Sheet content for overlay for marketplace
 *
 * @module views/components/PartnerInfoSheetContent
 * @memberof -Common
 */
import './PartnerInfoSheetContent.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Icon from '@ulta/core/components/Icon/Icon';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import ListGroup from '@ulta/modules/ListGroup/ListGroup';
import Markdown from '@ulta/modules/Markdown/Markdown';
import Text from '@ulta/modules/Text/Text';
/**
 * Represents a PartnerInfoSheetContent component
 *
 * @method
 * @param { PartnerInfoSheetContentProps} props - React properties passed from composition
 * @returns PartnerInfoSheetContent
 */
export const PartnerInfoSheetContent = function( props ){

  const { title, moreInfo, icon, linkAction, sellerInfo } = props;
  const { breakpoint } = useDeviceInflection();
  const isSmallDevice = breakpoint?.isSmallDevice();

  return (
    <div className='PartnerInfoSheetContent'>
      { icon && (
        <span className={ 'PartnerInfoSheetContent__Icon' } >
          <Icon
            name={ icon }
            size='xl'
            aria-hidden={ true }
          />
        </span>
      ) }
      { title && !isSmallDevice &&
      <div className='PartnerInfoSheetContent__Title'>
        <Text
          htmlTag={ 'h1' }
          textStyle='title-lg'
          id='overlay-title'
        >
          { title.text }
        </Text>
      </div>
      }
      {
        moreInfo && (
          <div className='PartnerInfoSheetContent__ListWrapper'>
            <ListGroup { ...spreadProps( { ...moreInfo } ) } />
          </div> )
      }
      { sellerInfo &&
      <div className='PartnerInfoSheetContent__SellerInfo'>
        <Markdown bodyStyle='body-2-noPadding'
          cmsMarkdownContent={ sellerInfo }
          alignment='left'
        />
      </div>
      }
      { linkAction &&
      <div className={ classNames(
        'PartnerInfoSheetContent__Link', {
          'PartnerInfoSheetContent--withSellerInfo': sellerInfo
        }
      ) }
      >
        <Link_Huge
          variant='primary'
          size='compact'
          action={ {
            ...linkAction
          } }
        >{ linkAction.label }</Link_Huge>
      </div>
      }
    </div>
  );
};

/**
 * Property type definitions
 * @typedef PartnerInfoSheetContentProps
 * @type {object}
* @property {object} props.title - Contains the title text and HTML tag.
* @property {string} propstitle.text - The text of the title.
* @property {string} props.title.htmlTag - The HTML tag to use for the title (e.g., 'h4').
* @property {object} props.moreInfo - Contains information for a ListGroup component.
* @property {array} props.moreInfo.items - Array of items for the ListGroup.
* @property {string} props.icon - Icon name to display.
* @property {object} props.linkAction - Configuration for a linkAction button.
* @property {string} props.sellerInfo - seller info
 */
export const propTypes =  {
  props: PropTypes.shape( {
    title: PropTypes.shape( {
      text: PropTypes.string,
      htmlTag: PropTypes.string
    } ),
    moreInfo: PropTypes.shape( {
      items: PropTypes.arrayOf(
        PropTypes.shape( {
          title: PropTypes.string,
          subTitle: PropTypes.string
        } )
      )
    } ),
    icon: PropTypes.string,
    linkAction: PropTypes.shape( {
      label: PropTypes.string,
      action: PropTypes.object
    } ),
    sellerInfo: PropTypes.string
  } )
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} props={}
 */
export const defaultProps =  {
  props: {}
};

PartnerInfoSheetContent.propTypes = propTypes;
PartnerInfoSheetContent.defaultProps = defaultProps;

export default PartnerInfoSheetContent;
